import React, { useEffect, useState } from 'react';
import { fetchPaymentsOverview } from '../services/api';
import { getApiBaseUrl } from '../utils/apiConfig';

export default function PaymentsOverview() {
    // Stripe state
    const [stripeData, setStripeData] = useState(null);
    const [loadingStripe, setLoadingStripe] = useState(true);
    const [stripeError, setStripeError] = useState(null);
    const [stripeCharges, setStripeCharges] = useState([]);
    const [loadingStripeCharges, setLoadingStripeCharges] = useState(true);
    const [stripeChargesError, setStripeChargesError] = useState(null);

    // PayPal state
    const [paypalCharges, setPaypalCharges] = useState([]);
    const [loadingPaypal, setLoadingPaypal] = useState(true);
    const [paypalError, setPaypalError] = useState(null);

    // Tips state
    const [tips, setTips] = useState([]);
    const [loadingTips, setLoadingTips] = useState(true);
    const [tipsError, setTipsError] = useState(null);
    const API_BASE = getApiBaseUrl();


    useEffect(() => {
        let fetchedTips = [];

        // Fetch Stripe Overview
        fetchPaymentsOverview()
            .then(res => {
                console.log('Stripe Overview Response:', res.data); // 👈 أضف هذا السطر
                setStripeData(res.data);
                setLoadingStripe(false);
            })
            .catch(() => {
                setStripeError('Failed to load payments overview');
                setLoadingStripe(false);
            });

        // Fetch Stripe Charges
        fetch(`${API_BASE}/api/charges`)
            .then(res => {
                if (!res.ok) throw new Error('Request failed');
                return res.json();
            })
            .then(data => {
                setStripeCharges(Array.isArray(data) ? data : []);
                setLoadingStripeCharges(false);
            })
            .catch(() => {
                setStripeChargesError('Failed to load Stripe charges');
                setLoadingStripeCharges(false);
            });

        // Fetch PayPal Charges
        fetch(`${API_BASE}/api/paypal/charges`)
            .then(res => {
                if (!res.ok) throw new Error('Request failed');
                return res.json();
            })
            .then(data => {
                setPaypalCharges(Array.isArray(data) ? data : []);
                setLoadingPaypal(false);
            })
            .catch(() => {
                setPaypalError('Failed to load PayPal charges');
                setLoadingPaypal(false);
            });

        // Fetch Tips and merge into charges
        fetch(`${API_BASE}/api/tips`)
            .then(res => {
                if (!res.ok) throw new Error('Request failed');
                return res.json();
            })
            .then(data => {
                const tipsArr = Array.isArray(data.tips) ? data.tips : [];
                setTips(tipsArr);           // <-- FIX HERE: setTips with the array, NOT whole object
                fetchedTips = tipsArr;

                // Merge tips into Stripe Charges
                setStripeCharges(prevCharges =>
                    Array.isArray(prevCharges) ? prevCharges.map(charge => {
                        const matchingTip = fetchedTips.find(tip => tip.chargeId === charge.id && tip.source === 'stripe');
                        return {
                            ...charge,
                            tip: matchingTip ? matchingTip.amount : 0,
                        };
                    }) : prevCharges
                );

                // Merge tips into PayPal Charges
                setPaypalCharges(prevCharges =>
                    Array.isArray(prevCharges) ? prevCharges.map(charge => {
                        const matchingTip = fetchedTips.find(tip => tip.chargeId === charge.id && tip.source === 'paypal');
                        return {
                            ...charge,
                            tip: matchingTip ? matchingTip.amount : 0,
                        };
                    }) : prevCharges
                );

                setLoadingTips(false);
            })
            .catch(() => {
                setTipsError('Failed to load tips');
                setLoadingTips(false);
            });
    }, [API_BASE]);

    const exportToCSV = (charges, filename) => {
        if (!charges.length) return;
        const headers = ['Charge ID', 'Gross (€)', 'Fee (€)', 'Net (€)', 'Charge Date', 'Payout Date', 'Tip (€)', 'Total (€)'];
        const rows = charges.map(c => [
            c.id,
            (c.gross - (c.tip || 0)).toFixed(2),
            (c.fee || 0).toFixed(2),
            (c.net || 0).toFixed(2),
            c.chargeDate,
            c.payoutDate || '-',
            (c.tip || 0).toFixed(2),
            (c.gross || 0).toFixed(2),
        ]);
        const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
    };

    const renderChargesTable = (charges) => {
        if (!Array.isArray(charges) || charges.length === 0) {
            return <p>No charges to display.</p>;
        }
        return (
        <table border="1" cellPadding="6" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 40 }}>
            <thead>
                <tr>
                    <th>Charge ID</th>
                    <th>Gross (€)</th>
                    <th>Fee (€)</th>
                    <th>Net (€)</th>
                    <th>Charge Date</th>
                    <th>Payout Date</th>
                    <th>Tip (€)</th>
                    <th>Total (€)</th>
                </tr>
            </thead>
            <tbody>
                {charges.map(charge => {
                    const tip = charge.tip || 0;
                    const grossWithoutTip = (Number(charge.gross) || 0) - (Number(charge.tip) || 0);
                    return (
                        <tr key={charge.id}>
                            <td>{charge.id}</td>
                            <td>{grossWithoutTip.toFixed(2)} €</td>
                            <td>{charge.fee.toFixed(2)} €</td>
                            <td>{charge.net.toFixed(2)} €</td>
                            <td>{charge.chargeDate}</td>
                            <td>{charge.payoutDate || '-'}</td>
                            <td>{tip.toFixed(2)} €</td>
                            <td>{charge.gross.toFixed(2)} €</td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
        );
    };

    const totalTips = (source) =>
        tips
            .filter(t => t.source === source)
            .reduce((acc, curr) => acc + curr.amount, 0);

    return (
        <section className="payments-overview" style={{ maxWidth: 900, margin: 'auto' }}>
            <h2>Payments Overview</h2>

            {/* Stripe Summary */}
            {loadingStripe && <p>Loading Stripe overview...</p>}
            {stripeError && <p style={{ color: 'red' }}>{stripeError}</p>}
            {stripeData && (
                <>
                    <p>Total Payments: {stripeData.totalPayments}</p>
                    <p>Total Revenue: €{(stripeData.totalRevenue || 0).toFixed(2)}</p>

                    <h3>By Payment Method</h3>
                    <ul>
                        {stripeData && stripeData.breakdown && Array.isArray(stripeData.breakdown) ? (
                            stripeData.breakdown.map(item => (
                                <li key={item._id}>
                                    {item._id}: {item.count} payments, €{item.totalAmount.toFixed(2)}
                                </li>
                            ))
                        ) : (
                            <li>No payment breakdown data available</li>

                        )}

                    </ul>
                </>
            )}

            {/* Stripe Charges */}
            <h3>Recent Stripe Charges</h3>
            {loadingStripeCharges ? (
                <p>Loading Stripe charges...</p>
            ) : stripeChargesError ? (
                <p style={{ color: 'red' }}>{stripeChargesError}</p>
            ) : (
                <>
                    <button onClick={() => exportToCSV(stripeCharges, 'stripe_charges.csv')} style={{ marginBottom: 10 }}>
                        Export Stripe Charges to CSV
                    </button>
                    {renderChargesTable(stripeCharges)}
                </>
            )}

            {/* PayPal Charges */}
            <h3>Recent PayPal Charges</h3>
            {loadingPaypal ? (
                <p>Loading PayPal charges...</p>
            ) : paypalError ? (
                <p style={{ color: 'red' }}>{paypalError}</p>
            ) : (
                <>
                    <button onClick={() => exportToCSV(paypalCharges, 'paypal_charges.csv')} style={{ marginBottom: 10 }}>
                        Export PayPal Charges to CSV
                    </button>
                    {renderChargesTable(paypalCharges)}
                </>
            )}

            {/* Tips Overview */}
            <h3>Tips Overview</h3>
            {loadingTips ? (
                <p>Loading tips...</p>
            ) : tipsError ? (
                <p style={{ color: 'red' }}>{tipsError}</p>
            ) : (
                <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: '400px' }}>
                    <thead>
                        <tr>
                            <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'left' }}>Source</th>
                            <th style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>Amount (€)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={{ border: '1px solid #ccc', padding: '8px' }}>Stripe Tips</td>
                            <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>{(totalTips('stripe') || 0).toFixed(2)}</td>
                        </tr>
                        <tr>
                            <td style={{ border: '1px solid #ccc', padding: '8px' }}>PayPal Tips</td>
                            <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right' }}>{(totalTips('paypal') || 0).toFixed(2)}</td>
                        </tr>
                        <tr>
                            <td style={{ border: '1px solid #ccc', padding: '8px', fontWeight: 'bold' }}>Total Tips</td>
                            <td style={{ border: '1px solid #ccc', padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>
                                {((totalTips('stripe') || 0) + (totalTips('paypal') || 0)).toFixed(2)}
                            </td>
                        </tr>
                    </tbody>
                </table>
            )}
        </section>
    );
} 
